import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { WinExcited, User, COLLECTION_NAMES } from '../types';

/**
 * Hook for managing Wins & Excited data with Firestore
 * Replaces Supabase queries with Firestore v9+ modular API
 */
export function useWinsExcited(userId: string | undefined) {
  const [winsExcited, setWinsExcited] = useState<WinExcited[]>([]);
  const { eosUsers = [] } = useAuth();
  const [loading, setLoading] = useState(true);

  // Load wins & excited with real-time listener
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const winsExcitedQuery = query(
      collection(db, COLLECTION_NAMES.WINS_EXCITED),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      winsExcitedQuery,
      (snapshot) => {
        const winsExcitedData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Convert Firestore Timestamps to Date objects
            createdAt: data.createdAt?.toDate?.() || data.createdAt || Timestamp.now(),
            meetingTimestamp: data.meetingTimestamp?.toDate?.() || data.meetingTimestamp,
          } as WinExcited;
        });
        setWinsExcited(winsExcitedData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading wins & excited:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  /**
   * Add a win or excited item
   * Saves to the wins_excited collection
   */
  const addWinExcited = useCallback(async (
    type: 'win' | 'excited',
    content: string,
    taggedUserId: string | null,
    currentUserId: string
  ) => {
    try {
      await addDoc(collection(db, COLLECTION_NAMES.WINS_EXCITED), {
        userId: currentUserId,
        type: type,
        content: content.trim(),
        taggedUserId: taggedUserId || null,
        createdAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error adding win/excited item:', error);
      throw error; // Re-throw to be caught by the modal
    }
  }, []);

  /**
   * Delete a win or excited item
   */
  const deleteWinExcited = useCallback(async (id: string): Promise<void> => {
    try {
      const docRef = doc(db, COLLECTION_NAMES.WINS_EXCITED, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting win/excited item:', error);
      throw error;
    }
  }, []);

  return {
    winsExcited,
    users: eosUsers,
    loading,
    addWinExcited,
    deleteWinExcited,
  };
}

